import Vue from 'vue';
import { mapState } from 'vuex';
import { getConfig, setConfig } from '@/config.ts';
import App from '@/app';
import createRouter from '@/router';
import store from '@/store';
import usePlugins from '@/plugins';
import { useServices } from '@/services/index.ts';
import hideLoader from '@/assets/util/loader';
import registerVersionWorker from '@/workers/register-version-worker';
import authorizationWrapper from '@/components/authorization-wrapper';
import generalError from '@/components/general-error/general-error';
import useModules from '@/use/modules';
import { APP } from '@/utils/event/constants.ts';
import '@/components/base-components/index.ts';
import '@/assets/libs/helpers';
import '@/assets/style/tailwind.css';
import { SECTION_ASSET_ESTIMATE } from '@/modules/estimate/router.js';
import { ESTIMATE, REPLICATOR } from '@/modules/list.ts';
import { DEV } from '@/store/modules/acl-access/constants.ts';
import { emitter } from '@/utils/event/emitter.ts';
import { socket } from '@/services/websocket.ts';

Vue.config.productionTip = false;
Vue.config.performance = !import.meta.env.PROD;

const options = {
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
};

const isGranted = (list, name, defaultValue = 'deny') => ['all', 'my'].includes(list?.[name] ?? defaultValue);

async function importModules({ pinia, router, store, aclAccess }) {
    const modules = [
        import('@/modules/calendah'),
        import('@/modules/connection'),
        import('@/modules/docs/index.ts'),
        import('@/modules/mapping'),
        import('@/modules/matching'),
        import('@/modules/migration/index.ts'),
        import('@/modules/monday'),
        import('@/modules/workspace/index.ts'),
    ];

    if (getConfig()?.ai?.enabled) {
        modules.push(import('@/modules/ai/index.ts'));
    }

    if (isGranted(aclAccess, ESTIMATE)
        || isGranted(aclAccess, SECTION_ASSET_ESTIMATE)
    ) {
        modules.push(import('@/modules/estimate'));
    }

    if (isGranted(aclAccess, REPLICATOR)) {
        modules.push(import('@/modules/replicator'));
    }

    if (import.meta.env.MODE === 'development' && isGranted(aclAccess, DEV.SANDBOX)) {
        modules.push(import('@/modules/dev-sandbox/index.js'));
    }

    useModules({ pinia, router, store }, await Promise.all(modules));
}

fetch('/config', options).then(response => response.json()).then(async (config) => {
    setConfig(config.window);

    useServices(Vue, {
        bugsnag: config.bugsnag,
        googleFilePicker: config.scripts?.google,
        websocket: config.websocket,
    });

    const { i18n, pinia } = usePlugins(Vue, { store, config });
    const router = createRouter({ store, i18n, config });

    await importModules({
        pinia,
        router,
        store,
        aclAccess: config.window?.aclAccess,
    });

    // public/js/manager/scripts.js
    window.config = config.window;

    await store.dispatch('aclAccess/SET_ACCESSES', config.window.aclAccess);
    await store.dispatch('user/SET_DATA', { data: config.user });

    new Vue({
        pinia,
        router,
        store,
        components: {
            authorizationWrapper,
            generalError,
        },
        created() {
            if (import.meta.env.PROD) {
                emitter.on(APP.UPDATE, (updated) => {
                    if (!updated) return;

                    store.dispatch('SET_VERSION_ACTUAL', false);
                });

                registerVersionWorker();
            }

            socket.on(APP.MAINTENANCE, (message) => {
                const { mode } = JSON.parse(message);

                store.dispatch('SET_MAINTENANCE', mode);
            });

            if (getConfig().cobranding?.value) {
                document.getElementById('app-favicon').href = `${import.meta.env.BASE_URL}img/cobranding/${getConfig().cobranding.value}/favicon.svg`;
            }

            hideLoader();
        },
        computed: {
            ...mapState('aclAccess', ['accesses']),
        },
        watch: {
            accesses(aclAccess) {
                importModules({
                    pinia,
                    router,
                    store,
                    aclAccess,
                });
            },
        },
        i18n,
        render: h => h(App),
    }).$mount('#app');
});
